// TermsOfService.js - Terms of Service legal page
// Linked from footer, Signup, and ProviderSignup
// Styled to match the rest of the app
// Last updated: April 2026

import React from 'react';

function TermsOfService() {
  return (
    <div className="legal-page">
      <div className="legal-container">

        <a href="/" className="auth-logo">Evermark Memorial</a>

        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-effective">Last updated: April 2026</p>

        {/* 1. Acceptance of Terms */}
        <div className="legal-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using the Evermark Memorial platform — including our website, mobile site, and any
            related services (collectively, the "Platform") — you agree to be bound by these Terms of
            Service ("Terms"). If you do not agree to these Terms, do not use the Platform.
          </p>
          <p>
            You must be at least 18 years of age to create an account or use the Platform. By using
            Evermark Memorial you represent that you are 18 or older and have the legal capacity to enter into
            a binding agreement.
          </p>
          <p>
            We may update these Terms from time to time. We will notify users of material changes by
            updating the date at the top of this page. Continued use of the Platform after changes are
            posted constitutes your acceptance of the revised Terms.
          </p>
        </div>

        {/* 2. Description of Service */}
        <div className="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            Evermark Memorial is an online marketplace platform that connects customers ("Customers") who wish
            to arrange grave care services with independent local service providers ("Caretakers") who
            fulfill those services. Evermark Memorial is a technology platform and marketplace operator — we
            are not a direct provider of grave care services.
          </p>
          <p>
            Services available through the Platform include one-time visit packages (Memory Visit, Care
            Visit, and Tribute Visit) and recurring monthly subscription plans (Remembrance, Guardian,
            and Legacy). Service descriptions and current pricing are displayed on the Platform at the
            time of booking.
          </p>
          <p>
            Evermark Memorial does not guarantee specific outcomes beyond photo delivery and reasonable care
            standards. We do not guarantee that a Caretaker will be available in every location or
            within any specific timeframe. We reserve the right to modify, suspend, or discontinue any
            part of the Platform at any time with or without notice.
          </p>
          <p>
            The Platform currently operates within the United States only.
          </p>
        </div>

        {/* 2a. Damage Limitation */}
        <div className="legal-section">
          <h2>Damage Limitation</h2>
          <p>
            Evermark Memorial and its Caretakers are not liable for damage to grave markers, monuments,
            decorations, or surrounding structures that results from:
          </p>
          <ul>
            <li>Hidden structural weaknesses or pre-existing cracks not visible prior to service</li>
            <li>Pre-existing conditions of the stone, base, or surrounding area</li>
            <li>Natural deterioration due to age, weathering, or environmental factors</li>
            <li>Fragile or unstable stones that shift or fall during or after routine cleaning</li>
            <li>Failure of old adhesives, mortar, or mounting hardware not installed by the Caretaker</li>
          </ul>
          <p>
            Customers are encouraged to note any known damage or fragile conditions in the booking notes
            before scheduling a visit. Caretakers will photograph the site before beginning work and
            immediately report any pre-existing issues discovered during the visit.
          </p>
        </div>

        {/* 2b. Refund Policy Summary */}
        <div className="legal-section">
          <h2>Refund Policy</h2>
          <p>
            Refunds are evaluated on a case-by-case basis. The following general guidelines apply:
          </p>
          <ul>
            <li><strong>Full refund:</strong> Job never claimed within 5 business days, cemetery inaccessible, cancelled before a Caretaker claims the job, or Caretaker unable to complete due to conditions beyond their control</li>
            <li><strong>Partial refund (50%):</strong> Incorrect grave location provided by the Customer that prevented completion</li>
            <li><strong>No refund:</strong> Job completed with before/after photos submitted, cancelled after a Caretaker has claimed the job, or decorations removed by cemetery staff after a completed visit</li>
          </ul>
          <p>
            For the full refund policy, processing times, and how to submit a refund request, please see our{' '}
            <a href="/refund-policy" style={{ color: '#4a7c59' }}>Refund Policy</a>.
          </p>
        </div>

        {/* 2c. Service Limitations */}
        <div className="legal-section">
          <h2>Service Limitations</h2>
          <p>The following limitations apply to all services booked through the Platform:</p>
          <ul>
            <li>Decorations placed during a visit may be removed by cemetery staff at any time — this is outside Evermark Memorial's control and does not qualify for a refund</li>
            <li>Severe weather, flooding, or unsafe site conditions may prevent or delay a visit — Caretakers will notify Customers promptly</li>
            <li>Cemetery regulations and staff restrictions take precedence over any service requested through the Platform</li>
            <li>Caretakers provide general maintenance and care only — they are not licensed landscapers, stone restorers, or monument repair technicians</li>
            <li>Preferred visit dates are estimates and not guaranteed — actual visit timing depends on Caretaker availability and site conditions</li>
            <li>Customers represent that they have the authority to authorize care for the grave site identified in their booking</li>
          </ul>
        </div>

        {/* 2d. Right to Refuse Service */}
        <div className="legal-section">
          <h2>Right to Refuse Service</h2>
          <p>Evermark Memorial and its Caretakers reserve the right to decline or cancel any booking that involves:</p>
          <ul>
            <li>Requests that are unsafe, hazardous, or that could endanger the Caretaker or others</li>
            <li>Disrespectful, abusive, or harassing communication toward Caretakers or staff</li>
            <li>Suspected vandalism, tampering, or unauthorized alteration of any grave site or monument</li>
            <li>Requests that violate cemetery rules, local ordinances, or applicable law</li>
            <li>Hostile, threatening, or discriminatory behavior toward any party</li>
            <li>Sites that are physically inaccessible or require equipment not available to the Caretaker</li>
            <li>Violations of these Terms of Service</li>
          </ul>
          <p>
            If a booking is declined or cancelled by Evermark Memorial or a Caretaker for any of the above
            reasons, a full refund will be issued. Repeated violations may result in account suspension.
          </p>
        </div>

        {/* 3. Independent Contractor Relationship */}
        <div className="legal-section">
          <h2>3. Independent Contractor Relationship</h2>
          <p>
            Caretakers who provide services through the Evermark Memorial Platform are independent contractors,
            not employees, agents, joint venturers, or partners of Evermark Memorial. Evermark Memorial does not
            supervise, direct, or control the manner, means, timing, or methods by which Caretakers
            perform services.
          </p>
          <p>
            Evermark Memorial is not responsible for and expressly disclaims all liability for the conduct,
            actions, omissions, negligence, or errors of any Caretaker. Customers engage Caretakers
            through the Platform at their own discretion.
          </p>
          <p>
            Caretakers set their own schedules and availability within the Platform. Evermark Memorial does
            not guarantee the availability of any specific Caretaker or the fulfillment of any booking
            within a particular time window.
          </p>
          <p>
            Caretakers are solely responsible for their own tax obligations, including filing and paying
            all applicable federal, state, and local taxes on earnings received through the Platform.
            Evermark Memorial will issue IRS Form 1099 to Caretakers who meet applicable thresholds.
          </p>
        </div>

        {/* 4. Customer Responsibilities */}
        <div className="legal-section">
          <h2>4. Customer Responsibilities</h2>
          <p>As a Customer, you agree to:</p>
          <ul>
            <li>
              Provide accurate cemetery name, location, and gravesite information when booking. Evermark
              Memorial and Caretakers are not responsible for visits to incorrect locations resulting from
              inaccurate information.
            </li>
            <li>
              Understand and comply with the rules and regulations of the relevant cemetery regarding
              decorations, flowers, and site modifications before placing a booking. Evermark Memorial does not
              verify individual cemetery policies.
            </li>
            <li>
              Report any disputes or service concerns within 24 hours of receiving the job completion
              notification. Disputes reported after this window will not be eligible for review.
            </li>
            <li>
              Maintain only one account per person. Creating multiple accounts to circumvent
              restrictions or abuse the dispute system is prohibited.
            </li>
            <li>
              Not contact or solicit Caretakers outside of the Platform for services related to those
              found through Evermark Memorial.
            </li>
          </ul>
        </div>

        {/* 5. Caretaker Responsibilities */}
        <div className="legal-section">
          <h2>5. Caretaker Responsibilities</h2>
          <p>As a Caretaker, you agree to:</p>
          <ul>
            <li>
              Provide services as described in the booked service package with reasonable care,
              skill, and professionalism.
            </li>
            <li>
              Take before and after photos on every visit as a condition of service completion and
              payment release. Jobs submitted without both required photos will not be marked complete.
            </li>
            <li>
              Upload a receipt photo along with your visit photos on all Tribute Visit jobs where a
              flower allowance has been issued.
            </li>
            <li>
              Maintain professional and respectful conduct at all times while on cemetery grounds and
              while communicating with Customers through the Platform.
            </li>
            <li>
              Comply with all cemetery rules, regulations, and posted notices at each site visited.
            </li>
            <li>
              Provide your own cleaning supplies, tools, transportation, and materials necessary to
              perform the booked service. Evermark Memorial does not supply equipment.
            </li>
            <li>
              Be solely responsible for your own tax obligations on all earnings received through the
              Platform. You will not be treated as an employee of Evermark Memorial for tax purposes.
            </li>
            <li>
              Not solicit Customers outside of the Platform to avoid Platform fees.
            </li>
            <li>
              Not accept payment directly from Evermark Memorial Customers for services discovered through
              the Platform outside of the Platform's payment system.
            </li>
          </ul>
        </div>

        {/* 6. Payments and Fees */}
        <div className="legal-section">
          <h2>6. Payments and Fees</h2>
          <p>
            All payments are processed securely through Stripe, Inc. By submitting a booking you
            authorize Evermark Memorial to charge the full amount shown at checkout, including any applicable
            service fees. A service fee of 3–5% may be added to the base service price at checkout.
          </p>
          <p>
            Customer funds are held in escrow by Evermark Memorial until the Caretaker marks the job as
            complete and both required photos have been uploaded. Following job completion, a 24-hour
            dispute window begins. If no dispute is filed within that window, Caretaker earnings are
            released for payout.
          </p>
          <p>
            Caretakers receive approximately 65% of the booking value after the Platform fee. Exact
            per-service earnings are displayed on the Caretaker dashboard before any job is claimed.
          </p>
          <p>
            For Tribute Visit jobs, a $22 flower allowance is released to the Caretaker upon job claim
            to cover the cost of a fresh floral arrangement. This allowance is non-refundable if the
            Caretaker abandons the job after claim. Caretakers are required to submit a receipt photo
            as documentation.
          </p>
          <p>
            All prices are listed in US dollars. Prices are subject to change and will be displayed
            accurately on the booking form at the time of purchase. Evermark Memorial reserves the right to
            modify pricing at any time.
          </p>
        </div>

        {/* 7. Cancellations and Refunds */}
        <div className="legal-section">
          <h2>7. Cancellations and Refunds</h2>
          <p>
            <strong>Before caretaker claim:</strong> Customers may cancel a booking before a Caretaker
            claims it for a full refund. Refunds will be processed through Stripe and may take 5–10
            business days to appear depending on your payment method.
          </p>
          <p>
            <strong>After caretaker claim:</strong> Once a Caretaker has claimed a job, cancellation
            may result in a partial refund at Evermark Memorial's discretion. The $22 flower allowance
            disbursed on Tribute Visit claims is non-refundable upon cancellation.
          </p>
          <p>
            <strong>After job completion:</strong> No refunds will be issued after a job is marked
            complete and the 24-hour dispute window has closed without a filed dispute.
          </p>
          <p>
            Evermark Memorial reserves the right to issue full or partial refunds at its discretion in cases
            of documented service failure, including failure to deliver required photos or failure to
            perform the booked service.
          </p>
        </div>

        {/* 8. Disputes */}
        <div className="legal-section">
          <h2>8. Disputes</h2>
          <p>
            Disputes regarding service quality or completion must be filed within 24 hours of receiving
            the job completion notification. To file a dispute, contact Evermark Memorial through the Contact
            page on the Platform.
          </p>
          <p>
            Evermark Memorial will review the before and after photos submitted by the Caretaker, any notes
            or messages in the booking thread, and any documentation provided by the Customer. Evermark
            Memorial acts as a mediator and will make a determination in its sole discretion.
          </p>
          <p>
            Evermark Memorial's decision on disputes is final. Outcomes may include a full refund, partial
            refund, rebooking credit, or dismissal of the dispute.
          </p>
          <p>
            Filing a fraudulent or bad-faith dispute — including disputes filed after personally
            verifying satisfactory service — may result in immediate account suspension and potential
            legal liability.
          </p>
        </div>

        {/* 9. Prohibited Conduct */}
        <div className="legal-section">
          <h2>9. Prohibited Conduct</h2>
          <p>You agree not to use the Evermark Memorial Platform to:</p>
          <ul>
            <li>Create false, duplicate, or fraudulent accounts</li>
            <li>File fraudulent or bad-faith disputes against Caretakers or Customers</li>
            <li>Harass, threaten, defame, or abuse any Caretaker, Customer, or Evermark Memorial team member</li>
            <li>
              Solicit Caretakers or Customers off-platform for the purpose of avoiding Platform fees
              or circumventing Platform policies
            </li>
            <li>
              Accept payment directly from Evermark Memorial Customers outside of the Platform's payment
              system for services discovered through Evermark Memorial
            </li>
            <li>
              Violate any cemetery's posted rules, regulations, or property rights while performing
              or arranging services
            </li>
            <li>
              Use the Platform for any purpose other than arranging or fulfilling legitimate grave
              care services as described
            </li>
            <li>Attempt to access other users' accounts, data, or private information</li>
            <li>Use automated bots, scrapers, or tools to access or extract Platform data</li>
            <li>Upload offensive, defamatory, illegal, or inappropriate content</li>
          </ul>
          <p>
            Violations may result in immediate account suspension or permanent termination, and may
            be reported to appropriate legal authorities.
          </p>
        </div>

        {/* 10. Intellectual Property */}
        <div className="legal-section">
          <h2>10. Intellectual Property</h2>
          <p>
            The Evermark Memorial name, logo, platform design, and all associated content are owned by
            Evermark Memorial and are protected by applicable trademark, copyright, and intellectual property
            laws. You may not use the Evermark Memorial name, logo, or brand assets without our prior written
            consent.
          </p>
          <p>
            Photos taken by Caretakers during visits are the work product of the Caretaker, delivered
            to the Customer through the Platform. Upon delivery, Customers receive a limited, personal,
            non-transferable license to use those photos for personal, non-commercial purposes only.
            Photos may not be redistributed, sold, or used for any commercial purpose without
            permission.
          </p>
          <p>
            Users may not reproduce, copy, distribute, or create derivative works from any Platform
            content without express written permission from Evermark Memorial.
          </p>
        </div>

        {/* 11. Limitation of Liability */}
        <div className="legal-section">
          <h2>11. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by applicable law, Evermark Memorial shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages arising from your use
            of the Platform, including but not limited to lost profits, lost data, or personal injury.
          </p>
          <p>
            Evermark Memorial is not liable for damage to headstones, grave markers, grave plots, or cemetery
            property caused by Caretakers. Caretakers bear sole responsibility for their conduct and
            the care with which they perform services.
          </p>
          <p>
            Evermark Memorial is not liable for outcomes affected by weather conditions, cemetery access
            restrictions, road closures, third-party interference, or other circumstances outside
            Evermark Memorial's reasonable control.
          </p>
          <p>
            Evermark Memorial's total liability to any user for any claim arising from use of the Platform
            shall not exceed the total amount paid by that user for the specific booking giving rise
            to the claim.
          </p>
          <p>
            Evermark Memorial carries general liability insurance for its Platform operations. The existence
            of this insurance does not expand Evermark Memorial's liability beyond the limits set forth in
            these Terms.
          </p>
        </div>

        {/* 12. Indemnification */}
        <div className="legal-section">
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Evermark Memorial, its founders, officers,
            employees, and agents from and against any claims, liabilities, damages, losses, and
            expenses — including reasonable legal fees — arising out of or related to your use of the
            Platform, your violation of these Terms, or your violation of any applicable law.
          </p>
          <p>
            Caretakers specifically agree to indemnify Evermark Memorial from any claims, damages, or liability
            arising from their performance of services, including claims by Customers, cemetery
            authorities, or third parties related to the Caretaker's conduct on cemetery grounds.
          </p>
        </div>

        {/* 13. Termination */}
        <div className="legal-section">
          <h2>13. Termination</h2>
          <p>
            Evermark Memorial reserves the right to suspend or permanently terminate any user account at any
            time and for any reason, including but not limited to violations of these Terms, fraudulent
            activity, or conduct that harms other users or the Platform's reputation.
          </p>
          <p>
            Caretaker accounts may be suspended or removed for poor platform ratings (consistently
            below 3.0), repeated job abandonment after claim, failure to submit required photos,
            or conduct violations as determined by Evermark Memorial in its sole discretion.
          </p>
          <p>
            Users may request deletion of their account at any time through the Platform or by
            contacting Evermark Memorial support. Account deletion will remove your profile information
            subject to applicable legal retention requirements.
          </p>
        </div>

        {/* 14. Governing Law */}
        <div className="legal-section">
          <h2>14. Governing Law</h2>
          <p>
            These Terms of Service are governed by and construed in accordance with the laws of the
            Commonwealth of Pennsylvania, without regard to its conflict of law provisions. You agree
            that any legal action or proceeding arising from or related to these Terms shall be brought
            exclusively in the state or federal courts located in Lawrence County, Pennsylvania, and
            you consent to the personal jurisdiction of those courts.
          </p>
        </div>

        {/* 15. Contact */}
        <div className="legal-section">
          <h2>15. Contact</h2>
          <p>
            For questions or concerns about these Terms of Service, please contact us through the{' '}
            <a href="/contact">Contact page</a> on the Evermark Memorial Platform. We will respond to
            inquiries within a reasonable time.
          </p>
        </div>

        <p className="legal-footer-note">
          Also see our <a href="/privacy">Privacy Policy</a> and{' '}
          <a href="/contact">Contact &amp; Support</a> page.
        </p>

      </div>
    </div>
  );
}

export default TermsOfService;
